import {CasinoStatus, ListMode} from "./CasinoReducer";
import {Dispatch} from "redux";
import {getContainer} from "../ioc/IOCSetup";
import {AppState} from "../RootReducer";
import {
    CasinoCurrencies,
    CasinoGameInfo,
    CasinoLobby,
    CasinoLobbyCategory,
    CasinoLobbyCategoryType,
    CasinoPage,
    CasinoSortedLobby,
    CasinoStudio,
    GameOrder,
    GameTag,
    Jackpot
} from "../http/protocol";
import {fetchAndParseContent} from "../contenful/ContentfulActions";
import {ContentModelName} from "../contenful/types";
import {
    CasinoStudioLink,
    CategoryGames,
    CategoryStudios,
    getCasinoState,
    includeGame,
    isSupportedCurrency,
    isSupportedPlatform,
    isGameAllowedByJurisdictions
} from "../selectors/casino-selectors";
import {getActiveCurrencyCode, getCountry, getLoggedIn} from "../selectors/user-selectors";

export enum CasinoActionTypes {
    SELECT_TAB = "@casino/tab/select",
    GAMES_REQUEST = "@casino/games/request",
    GAMES_RESPONSE = "@casino/games/response",
    GAMES_ERROR = "@casino/games/error",
    SELECT_LIST_MODE = "@casino/list/mode",
    UPDATE_SEARCH_VALUE = "@casino/update/search",
    SET_PLAYED_GAMES  = "@casino/set/games",
    SET_FAVOURITE_GAMES  = "@casino/set/favourite",
    SET_SELECTED_GAMES = "@casino/select/games",
    SHOW_ALL_STUDIOS = "@casino/show/studios",
    CLEAR_FILTERS = "@casino/clear/filters",
    SELECT_MOBILE_PREVIEW = "@casino/select/mobile/preview",
    SELECT_CATEGORY = "@casino/select/category",
    JACKPOTS_RESPONSE = "@casino/jackpots/response",
    SET_CASINO_TOP_BANNER_HEIGHT = "@casino/banner/height",
    SET_FILTER_ACTIVE_BONUS_GAMES = "@casino/filter/active-bonus",
    SELECT_FILTER = "@casino/filter/select"
}

export interface GamesByPage {
    pageId : number;
    ids? : number[];
}
export type CasinoAction =
    | { type : CasinoActionTypes.GAMES_RESPONSE, lobby : CasinoLobby, gamesInPage : PageGames[], favouritesByPage : GamesByPage[], recentlyPlayedByPage : GamesByPage[]}
    | { type : CasinoActionTypes.SELECT_TAB, tabId? : number, pageId?: number}
    | { type : CasinoActionTypes.SELECT_CATEGORY, catId? : number}
    | { type : CasinoActionTypes.GAMES_ERROR, error : Error}
    | { type : CasinoActionTypes.GAMES_REQUEST}
    | { type : CasinoActionTypes.SELECT_LIST_MODE, mode : ListMode}
    | { type : CasinoActionTypes.UPDATE_SEARCH_VALUE, value : string}
    | { type : CasinoActionTypes.SET_PLAYED_GAMES, gamesByPage : GamesByPage}
    | { type : CasinoActionTypes.SET_FAVOURITE_GAMES, gamesByPage : GamesByPage}
    | { type : CasinoActionTypes.SHOW_ALL_STUDIOS, show : boolean}
    | { type : CasinoActionTypes.CLEAR_FILTERS}
    | { type : CasinoActionTypes.SET_SELECTED_GAMES, games : CasinoGameInfo[] }
    | { type : CasinoActionTypes.JACKPOTS_RESPONSE, jackpots : Jackpot[]}
    | { type : CasinoActionTypes.SET_CASINO_TOP_BANNER_HEIGHT, bannerHeight? : number}
    | { type : CasinoActionTypes.SET_FILTER_ACTIVE_BONUS_GAMES, isFilterActiveBonusGames: boolean}
    | { type : CasinoActionTypes.SELECT_FILTER};

export const setBannerHeight = (bannerHeight? : number) => (
    { type : CasinoActionTypes.SET_CASINO_TOP_BANNER_HEIGHT, bannerHeight : bannerHeight}
)
export const showAllStudios = (show : boolean) => (
    { type : CasinoActionTypes.SHOW_ALL_STUDIOS, show : show}
)

export const selectFilter = () => ({ type : CasinoActionTypes.SELECT_FILTER})

async function extractSelectedGames(studioIds: number[],
                               tagIds: number[],
                               searchValue: string,
                               allGamesInPage: CasinoGameInfo[],
                               studios : CasinoStudio[],
                               country? : string,
                               currencies? : CasinoCurrencies[],
                               activeCurrency? : string,
                               loggedIn? : boolean,
                               hideRestricted? : boolean) {


    const chunks = splitToChunks(allGamesInPage, 200);

    let promises : Promise<CasinoGameInfo[]>[] = chunks.map((games : CasinoGameInfo[])=> {
        return doInNewEvent(()=>{

            let filtered : CasinoGameInfo[] = games;
            if(searchValue && searchValue.length > 1) {
                filtered = filtered.filter(g => includeGame(g, studios, country,currencies ?? [], activeCurrency, loggedIn, hideRestricted)
                    && g.name && g.name.toLocaleLowerCase().indexOf(searchValue.toLocaleLowerCase()) != -1);
            }

            if(studioIds && studioIds.length > 0) {
                filtered = filtered.filter(g =>  studioIds.find( s =>
                    s === g?.studioId && includeGame(g, studios, country,currencies ?? [], activeCurrency, loggedIn, hideRestricted)
                ));
            }

            if(tagIds && tagIds.length > 0) {
                filtered = filtered.filter(g => {
                    if(g.gameTags && g.gameTags.length > 0) {
                        return  includeGame(g, studios, country,currencies ?? [], activeCurrency, loggedIn, hideRestricted) &&
                          !!g.gameTags.find((tagId : number) => tagIds.indexOf(tagId) != -1);
                    } else {
                        return false;
                    }
                });
            }
            return filtered;
        });
    })

    let resultChunks = await Promise.all(promises);

    return resultChunks.flat(1);
}

function getGamesByPage(state : AppState, pageId : number) : CasinoGameInfo[] {
    let currentPage = state.casino.lobby?.pages?.find(p => p.id == pageId);
    let flat = currentPage?.games ?? [];
    return flat.map(id =>  state.casino.lobby?.games?.["" + id] ?? {});

}
function getStudiosByPage(state : AppState, pageId : number) : CasinoStudio[] {
    let currentPage = state.casino.lobby?.pages?.find(p => p.id == pageId);
    let flat = currentPage?.studios ?? [];
    let studios : CasinoStudio[] = [];
    flat.map(id => {
        let studio = state.casino.lobby?.studios?.["" + id];
        if(studio) {
            studios.push(studio);
        }
    })
    return studios;
}
export async function selectGamesByFilter(state: AppState, studioIds: number[], tags: number[], search?: string, currentPageId?: number) {
    let games : CasinoGameInfo[] = Object.values(state.casino.lobby?.games ?? {});
    let studios : CasinoStudio[] = Object.values(state.casino.lobby?.studios ?? {});
    let hideRestricted = true;
    if(currentPageId) {
        games = getGamesByPage(state, currentPageId);
        studios = getStudiosByPage(state, currentPageId);
        hideRestricted = state.casino.lobby?.pages?.find(p => p.id == currentPageId)?.hideCountryRestrictedGames ?? true;
    }
    let country = getCountry(state);
    let currencies = getCasinoState(state).lobby?.currencies ?? [];
    let activeCurrency = getActiveCurrencyCode(state);
    let loggedIn = getLoggedIn(state);

    let extracted = await extractSelectedGames(studioIds, tags ?? [], search ?? "", games, studios, country, currencies, activeCurrency, loggedIn, hideRestricted);

    return extracted
}

export const clearFilters = () => (
    { type : CasinoActionTypes.CLEAR_FILTERS}
)

export const updateSearchValue = (value :string, currentPageId? : number, studioIds: number[] = [], tags: number[] = []) => {
    return async (dispatch : Dispatch, getState : () => AppState) => {
        dispatch({ type : CasinoActionTypes.UPDATE_SEARCH_VALUE, value : value});
        let state = getState();
        let games = await selectGamesByFilter(state, studioIds, tags, value, currentPageId);
        const jurisdictionId = getState().settings.jurisdictionId;
        games = games.filter(game => isGameAllowedByJurisdictions(game, jurisdictionId ?? ""));
        dispatch({type: CasinoActionTypes.SET_SELECTED_GAMES, games: games});
    }
}

export const selectTab = (tabId? : number, pageId? : number) => {
    return (dispatch : Dispatch, getState : () => AppState) => {
        if(!pageId) {
            pageId = getState().casino.lobby?.pages?.find(p => p.defaultPage)?.id;
        }
        if(!tabId) {
            let page =  getState().casino?.lobby?.pages?.find(p => p.id == pageId);
            tabId = page?.tabs?.[0].id ?? undefined;
        }

        return dispatch(  { type : CasinoActionTypes.SELECT_TAB, tabId : tabId, pageId : pageId})
    }
}

export const selectCategory = (catId?: number) => {
   return { type : CasinoActionTypes.SELECT_CATEGORY, catId : catId};
}

export const setIsFilterActiveBonusGames = (isFilterActiveBonusGames: boolean) => ({ type : CasinoActionTypes.SET_FILTER_ACTIVE_BONUS_GAMES, isFilterActiveBonusGames})




const RECENTLY_PLAYED_GAMES_KEY = "recentlyPlayedGames";
const FAVOURITE_GAMES_KEY = "favouriteGames";
const NO_GAMES_IN_RECENTLY_PLAYED = 6;

const getPageOrDefault = (
  pageId: number | undefined,
  pages: CasinoPage[]
): CasinoPage | null => {
  const pageWithId = pages?.find((p) => p.id === pageId);
  if (pageWithId) {
    return pageWithId;
  }

  const defaultPage = pages?.find((p) => p.defaultPage);
  if (defaultPage) {
    return defaultPage;
  }

  return null;
};

function addGameIdToPageOrLast(
    pageId: number,
    gameId: number,
  gamePages: GamesByPage[]
) {
  const updatedPages = [...gamePages];
  let updatedIds: number[] = [];

  for (let i = 0; i < updatedPages.length; i++) {
    const isEndOfArray = i === updatedPages.length - 1;
    const isPageToUpdate = updatedPages[i].pageId === pageId;

    if (isEndOfArray && !isPageToUpdate) {
      updatedIds = [gameId];

      updatedPages.push({
        pageId: pageId,
        ids: updatedIds,
      });
    }

    if (!isPageToUpdate) continue;

    updatedIds = [
      gameId,
      // If they game is already in the list, we want to show it first
      ...(updatedPages[i].ids?.filter((id) => id !== gameId) ?? []),
    ];
    updatedIds = updatedIds.slice(0, Math.min(NO_GAMES_IN_RECENTLY_PLAYED, updatedIds.length || 1));

    updatedPages[i] = {
      ...updatedPages[i],
      ids: updatedIds,
    };
    break;
  }

  return {
    updatedPages,
    updatedIds,
  };
}

export const addPlayedGame = (game: CasinoGameInfo, pageId?: number) => {
  return (dispatch: Dispatch, getState: () => AppState) => {
    const { recentlyPlayedGamesByPage = [], lobby = {} } = getState().casino;
    const pageToUpdate = getPageOrDefault(pageId, lobby.pages ?? []);

    if (!pageToUpdate) {
      console.error("No page found - No default page found");
      return;
    }

    const { updatedPages, updatedIds } = addGameIdToPageOrLast(
        pageToUpdate?.id ?? -1,
        game?.id ?? -1,
        recentlyPlayedGamesByPage
    );

    dispatch({
      type: CasinoActionTypes.SET_PLAYED_GAMES,
      gamesByPage: updatedPages,
    });
    localStorage.setItem(
      RECENTLY_PLAYED_GAMES_KEY + (pageToUpdate.defaultPage ? "" : "-p-" + pageId),
      JSON.stringify(updatedIds)
    );
  };
};

export const toggleFavouriteGame = (game : CasinoGameInfo, pageId? : number) => {
    return (dispatch : Dispatch, getState : () => AppState) => {

        let gamesByPage = getState().casino?.favouriteGamesByPage ?? [];

        gamesByPage = gamesByPage.concat();
        let pageById = getState().casino.lobby?.pages?.find(p => p.id == pageId);
        if(!pageById) {
            pageById = getState().casino.lobby?.pages?.find(p => p.defaultPage == true);
        }

        if(pageById) {
            pageId = pageById.id ?? -1;

            let index = gamesByPage.findIndex(gbp => gbp.pageId == pageId);
            if(index < 0) {
                gamesByPage.push({pageId : pageId, ids: []});
                index = gamesByPage.length - 1;
            }

            let games = gamesByPage.find(g => g.pageId == pageId)?.ids ?? [];
            let update = games.filter(g => g != game.id);
            if(games.length == update.length) {
                update = update.concat([game?.id ?? -1]);
            }

            gamesByPage[index] = {
                pageId : pageId,
                ids : update
            }


            dispatch({ type : CasinoActionTypes.SET_FAVOURITE_GAMES,  gamesByPage : gamesByPage});
            localStorage.setItem(FAVOURITE_GAMES_KEY + (pageById.defaultPage ? "" : "-p-"+pageId), JSON.stringify(update));


        }

    }
}

export interface RecentlyAndFavourites {
    recently : GamesByPage[];
    favourites : GamesByPage[];
}
export const loadRecentlyPlayedAndFavourites = (pages : CasinoPage[]) : RecentlyAndFavourites  => {
    let recently = loadRecentlyPlayedGames(pages);
    let favourites = loadFavouriteGames(pages);

    return {
        recently : recently,
        favourites : favourites
    }
}
export const loadRecentlyPlayedGames = (pages : CasinoPage[]) : GamesByPage[] => {

        try {
                let gameIdsPerPage : GamesByPage[] = pages.filter(p => !p.defaultPage).map(p => {
                    try {
                        let pageFav = localStorage.getItem(RECENTLY_PLAYED_GAMES_KEY + "-p-"+p.id);
                        if(pageFav && p.id) {
                            return { pageId : p.id, games : [], ids : JSON.parse(pageFav) as number[]};
                        }
                    } catch (e) {
                        console.log("Unable to load favourites for page ", p.id);
                    }
                    return { pageId: -1, games : [],  ids : []};
                });

                let defaultPage = pages.find(p => p.defaultPage == true);
                if(defaultPage) {
                    let gameIds : number[] = [];
                    try {
                        let games = localStorage.getItem(RECENTLY_PLAYED_GAMES_KEY);
                        if(games && games.length > 0) {
                            gameIds = JSON.parse(games);
                        } else {
                            gameIds = [];
                        }
                    } catch (e) {
                        gameIds = [];
                    }


                    gameIdsPerPage.push({ pageId : defaultPage.id ?? -1, ids : gameIds});
                }


                return gameIdsPerPage;
        } catch (e) {
            console.log("Unable to parse recently played games");
        }

    return [];
}
export const loadFavouriteGames = (pages : CasinoPage[]) : GamesByPage[] => {
    try {

        let gameIdsPerPage : GamesByPage[] = pages.filter(p => !p.defaultPage).map(p => {
            try {
                let pageFav = localStorage.getItem(FAVOURITE_GAMES_KEY + "-p-"+p.id);
                if(pageFav && p.id) {
                    return { pageId: p.id, games : [], ids :  JSON.parse(pageFav) as number[]};
                }
            } catch (e) {
                console.log("Unable to load favourites for page ", p.id);
            }
            return { pageId: -1, games : [], ids : []};
        });
        let defaultPage = pages.find(p => p.defaultPage == true);
        if(defaultPage) {
            let favourites = localStorage.getItem(FAVOURITE_GAMES_KEY);
            let gameIds : number[] = [];
            if(favourites) {
                gameIds = JSON.parse(favourites);
            }
            gameIdsPerPage.push({ pageId : defaultPage.id ?? -1, ids : gameIds });
        }

        return gameIdsPerPage;
    } catch (e) {
        console.log("Unable to parse recently played games");
    }

    return  [];

}
export const requestGames = () => (
    { type : CasinoActionTypes.GAMES_REQUEST}
)
export const gamesResponse = (lobby : CasinoSortedLobby, favourites : GamesByPage[], recently : GamesByPage[])  => (
    { type : CasinoActionTypes.GAMES_RESPONSE, lobby : lobby, favouritesByPage : favourites, recentlyPlayedByPage : recently}
)
export const gamesError = (error : Error) => (
    { type : CasinoActionTypes.GAMES_ERROR, error : error }
)
export const selectListMode = (mode : ListMode) => (
    { type : CasinoActionTypes.SELECT_LIST_MODE, mode : mode }
);


export interface PageGames { id? : number, games? : CasinoGameInfo[] }
export interface CategoriesByPage {
    id? : number;
    studioIds? : number[];
    categories? : CategoryGames[];
    tags?: GameTag[];
    studios? : CasinoStudio[];
    restrictedStudios? : CasinoStudio[];
    categoryStudios? : CategoryStudios[];
    availableFavourites? : CasinoGameInfo[];
    availableRecently? : CasinoGameInfo[];
}
export interface PageStudio { id?: number, studios? : number[] }
export const getStudiosPerPage = (pageGames : CategoriesByPage[]) : PageStudio[] => {
    return pageGames.map(pg => {
        return {
            id : pg.id,
            studios : pg.studioIds
        }
    })
}

export function isGameAvailable(g: CasinoGameInfo, currencies: CasinoCurrencies[], activeCurrency: string | undefined, loggedIn: boolean | undefined) {
    if (!isSupportedPlatform(g)) {
        return false;
    }
    if (!isSupportedCurrency(g, currencies, activeCurrency, loggedIn)) {
        return false;
    }

    return true;
}

export function extractStudios(c : CasinoLobbyCategory, studios: CasinoStudio[], games? : CasinoGameInfo[]) {
    let studiosFromIds = c.studioIds?.filter(sId => !!studios.find(s => s.id === sId));
    let filteredStudios : CasinoStudioLink[] = studiosFromIds
        ?.map(sId => { return { studio: studios.find(s => s.id === sId) ?? {}} }) ?? [];

    let studiosFromGames = games?.map(g => {
        let s : CasinoStudioLink = {
            studio : studios.find(s => s.id == g.studioId),
            game : g
        };
        return s
    }) ?? [];

    return filteredStudios.concat(studiosFromGames);
}

export const doInNewEvent = async <E>(fn : () => E) : Promise<E> => {
    return new Promise((resolve,reject) => {
        setTimeout(()=> {
            try {
                let res : E  = fn();
                resolve(res);
            } catch (e){
                reject(e);
            }
        },0);
    })
}

function splitToChunks(games: CasinoGameInfo[], itemsPerChunk : number) {


    const slices = games.reduce<CasinoGameInfo[][]>((resultArray, item, index) => {
        const chunkIndex = Math.floor(index / itemsPerChunk)

        if (!resultArray[chunkIndex]) {
            resultArray[chunkIndex] = [] // start a new chunk
        }

        resultArray[chunkIndex].push(item)

        return resultArray
    }, [])
    return slices;
}

export const fetchJackpots = () => {
    return async (dispatch : Dispatch) => {
        let jackpotList = await getContainer().getCasinoService().getJackpots();
        dispatch({ type : CasinoActionTypes.JACKPOTS_RESPONSE, jackpots : jackpotList.jackpots ?? []})
    }
}
export const loadCasinoGames = () => {
    return async (dispatch : Dispatch<any>, getState : () => AppState )=>{

        let state = getState();
        let status = state.casino.status;
        if(status != CasinoStatus.DONE && status != CasinoStatus.LOADING) {
            dispatch(requestGames());
            try {

                let lobby = await  getContainer().getCasinoService().getSortedLobby();
                let contentCategoryKeys: string[] = (lobby.pages?.flatMap(p => p.tabs).flatMap(t => t?.categories).map(c => c?.contentKey as string).filter(ck => ck)) ?? []
                contentCategoryKeys = Array.from(new Set(contentCategoryKeys));
                if(!getContainer().getStrapiClient().isEnabled()) {
                    await dispatch(fetchAndParseContent(ContentModelName.GAMELISTSECTION, undefined, true, contentCategoryKeys));
                }
                let loaded = loadRecentlyPlayedAndFavourites(lobby.pages ?? []);
                dispatchLobbyUpdate(dispatch, lobby, state, loaded);

            } catch (e) {
                dispatch(gamesError(e as Error));
            }
        }
    }
}

function dispatchLobbyUpdate(dispatch : Dispatch, lobby : CasinoSortedLobby, state : AppState, recently : RecentlyAndFavourites) {
    dispatch(gamesResponse(lobby, recently.favourites, recently.recently));
}
function hasId(id? : number, ids? : number[]) {
    if(!id || !ids) {
        return false;
    }
    return ids.some(i => i == id);
}
function toNumber(d : any) : number {
    if(typeof d == "number"){
        return d as number;
    } else if(typeof d == "object") {
        return d.getTime?.() ?? 0;
    }
    return 0;
}
function sortByManualGames(a : CasinoGameInfo, b: CasinoGameInfo, cat : CasinoLobbyCategory) {
    if(cat.games && cat.games.length > 0 && typeof(a.id) != "undefined" && typeof(b.id) != "undefined") {
        let aIndex = cat.games.indexOf(a.id);
        let bIndex = cat.games.indexOf(b.id);
        if(aIndex < 0 && bIndex >= 0) {
            return -1;
        } else if(aIndex >= 0 && bIndex < 0) {
            return 1;
        } else if(aIndex >= 0 && bIndex >= 0){
            return bIndex - aIndex;
        } else {
            return Number.MAX_SAFE_INTEGER;
        }
    } else {

        return Number.MAX_SAFE_INTEGER;
    }
}


function isGameInCategory(cat: CasinoLobbyCategory, mobile: boolean | undefined, g: CasinoGameInfo) {
    const matchAll = cat.matchAllSelectedTags;

    if (mobile && (!g.mobileExternalId || g.mobileExternalId.length == 0)) {
        return false;
    } else if (!mobile && (!g.externalId || g.externalId.length == 0)) {
        return false;
    }

    if (cat.games && cat.games.length > 0) {
        if (cat.games.some(id => id == g.id)) {
            return true;
        }
    }
    if(cat.categoryType == CasinoLobbyCategoryType.STUDIO_SELECTION || cat.categoryType == CasinoLobbyCategoryType.CONTENT) {
        return false;
    }

    //if studio id's are present then we need to match on it
    let studioMatch = false;
    if (cat.studioIds && cat.studioIds.length > 0) {
        let hasStudio = cat.studioIds.find(id => id === g.studioId);
        if (!hasStudio) {
            return false;
        } else {
            studioMatch = true;
        }
    }

    if (cat.tags && cat.tags.length > 0) {
        if (matchAll) {
            if (cat.tags.every(t => hasId(t, g.gameTags))) {
                return true;
            }
        } else {
            if (g.gameTags && g.gameTags.some((t) => hasId(t, cat.tags))) {
                return true;
            }
        }
    }
    if (cat.gameTypes && cat.gameTypes.length > 0) {
        if (g.gameType && cat.gameTypes.some(type => type == g.gameType)) {
            return true;
        }
    }

    // we need a match for type or tag as well as studio match,
    // if tags or types are empty it is considered as a match
    let hasTypes = (cat.gameTypes ?? []).length > 0;
    let hasTags = (cat.tags ?? []).length > 0;
    if (studioMatch == true && !hasTypes && !hasTags) {
        return true;
    }

    return false;
}

export function gameInPage(g :  CasinoGameInfo, p? : CasinoPage, mobile? : boolean) : boolean {
    if(!p) {
        return true;
    }
    let res =  !!p.tabs?.find(t => {
        return (t.categories ?? []).find((cat : CasinoLobbyCategory) => {
            return isGameInCategory(cat, mobile, g);

        });

    });

    return res;
}

function sortGamesForCategory(cat: CasinoLobbyCategory, games: CasinoGameInfo[]) {
    if (cat.gameOrder == GameOrder.ALPHABETICAL) {
        games.sort((a, b) => {
            let manual = sortByManualGames(a, b, cat);
            if (manual == Number.MAX_SAFE_INTEGER) {
                let res = (a.name ?? "").localeCompare(b.name ?? "");
                return res;

            } else {
                return -manual;
            }
        })

    } else if (cat.gameOrder == GameOrder.RELEASE_DATE) {
        games.sort((a, b) => {
            let manual = sortByManualGames(a, b, cat);
            if (manual == Number.MAX_SAFE_INTEGER) {
                let res = -(toNumber(a.releaseDate) - toNumber(b.releaseDate));
                return res;
            } else {
                return -manual;
            }

        });
    } else {
        //the default game order received from the backend is popularity
        games.sort((a, b) => {
            let res = sortByManualGames(a, b, cat);
            return res == Number.MAX_SAFE_INTEGER ? 0 : -res;
        });
    }
}

export function extractGamesByCategory(categories : CasinoLobbyCategory[], games : CasinoGameInfo[], mobile : boolean) : CasinoGameInfo[][]{
    return categories.map((cat : CasinoLobbyCategory) => {
        const matchAll = cat.matchAllSelectedTags;
        let filteredGames = games.filter(g => isGameInCategory(cat, mobile, g));
        sortGamesForCategory(cat, filteredGames);
       return filteredGames;

    });

}
export const generateImgUrl = (game: string, studio: string, template: string) => {
    return encodeURI(template.replace(/\$studioname/g, studio).replace(/\$studio/g, studio.replace(/[^a-z0-9]/gi, '').toLowerCase()).replace(/\$game/g, game.replace(/[^a-z0-9]/gi, '').toLowerCase()));
}

export const getStudio = (studio: { [index: string]: CasinoStudio }, id:number): string => {
    return Object.values(studio).find(v => v.id == id)?.name as string
};

export const updateGamesWithImageUrl = (studios: { [index: string]: CasinoStudio }, games: CasinoGameInfo[], template: string) => {
    return games?.map(game => {
        if(game.imageVariants && game.imageVariants.length){
            return {...game, imageUrl: generateImgUrl(game?.name as string , getStudio(studios, (game.studioId as number)), template)}
        } else {
            return game
        }
    })
}

export class setCategoryLayout{
    private flex: string;
    private maxWidth: string;

    constructor(val: number){
        this.flex = `0 0 ${this.caluclatePercentage(val)}%`;
        this.maxWidth = `${this.caluclatePercentage(val)}%`;
    }
    caluclatePercentage(val: number){
        return (100 / val).toFixed(3);
    }
}
